import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  FileText,
  FileCode,
  Brain,
  Image,
  Database,
  Camera,
  ChevronDown,
  ChevronUp,
  Minimize2,
  Maximize2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_URL } from '@/config/api';

interface ProcessingStep {
  id: string;
  name: string;
  icon: React.ElementType;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  startTime?: Date;
  endTime?: Date;
}

interface PageProgress {
  pageId: string;
  url: string;
  title: string;
  steps: ProcessingStep[];
  overallStatus: 'pending' | 'processing' | 'completed' | 'failed';
}

interface ReprocessingProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  totalPages: number;
}

const PROCESSING_STEPS = [
  { id: 'html', name: 'HTML Extraction', icon: FileCode },
  { id: 'text', name: 'Text Extraction', icon: FileText },
  { id: 'semantic', name: 'Semantic Analysis', icon: Brain },
  { id: 'images', name: 'Image Extraction', icon: Image },
  { id: 'embeddings', name: 'Embeddings Generation', icon: Database },
  { id: 'screenshots', name: 'Screenshot Capture', icon: Camera },
];

export function ReprocessingProgressDialog({
  open,
  onOpenChange,
  siteId,
  totalPages,
}: ReprocessingProgressDialogProps) {
  const [pageProgress, setPageProgress] = useState<Map<string, PageProgress>>(new Map());
  const [isMinimized, setIsMinimized] = useState(false);
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);
  const startTimeRef = useRef<Date>(new Date());

  console.log('[ReprocessingProgressDialog] Rendered with:', { open, siteId, totalPages });

  useEffect(() => {
    console.log('[ReprocessingProgressDialog] Effect triggered, open:', open, 'socket:', !!socketRef.current);
    if (open && !socketRef.current) {
      console.log('[ReprocessingProgressDialog] Initializing socket connection');
      // Initialize socket connection
      const socket = io(window.location.origin, {
        path: '/socket.io/',
        transports: ['websocket', 'polling'],
      });

      socketRef.current = socket;

      // Join reprocessing room for this site
      socket.emit('join-room', `reprocessing:${siteId}`);

      // Listen for progress updates
      socket.on(`reprocessing:${siteId}:progress`, (data: any) => {
        setPageProgress(prev => {
          const newProgress = new Map(prev);
          const existing = newProgress.get(data.pageId) || createPageProgress(data);
          
          // Update step status
          const stepIndex = existing.steps.findIndex(s => s.id === data.stepId);
          if (stepIndex !== -1) {
            existing.steps[stepIndex].status = data.status;
            if (data.error) {
              existing.steps[stepIndex].error = data.error;
            }
            if (data.status === 'processing') {
              existing.steps[stepIndex].startTime = new Date();
            } else if (data.status === 'completed' || data.status === 'failed') {
              existing.steps[stepIndex].endTime = new Date();
            }
          }

          // Update overall status
          existing.overallStatus = calculateOverallStatus(existing.steps);
          newProgress.set(data.pageId, existing);
          
          return newProgress;
        });
      });

      // Listen for initial page list
      socket.on(`reprocessing:${siteId}:start`, (data: any) => {
        const initialProgress = new Map<string, PageProgress>();
        data.pages.forEach((page: any) => {
          initialProgress.set(page.id, {
            pageId: page.id,
            url: page.url,
            title: page.title || 'Untitled',
            steps: PROCESSING_STEPS.map(step => ({
              ...step,
              status: 'pending',
            })),
            overallStatus: 'pending',
          });
        });
        setPageProgress(initialProgress);
        startTimeRef.current = new Date();
      });

      return () => {
        socket.emit('leave-room', `reprocessing:${siteId}`);
        socket.disconnect();
        socketRef.current = null;
      };
    }
  }, [open, siteId]);

  const createPageProgress = (data: any): PageProgress => ({
    pageId: data.pageId,
    url: data.url || '',
    title: data.title || 'Untitled',
    steps: PROCESSING_STEPS.map(step => ({
      ...step,
      status: step.id === data.stepId ? data.status : 'pending',
    })),
    overallStatus: 'processing',
  });

  const calculateOverallStatus = (steps: ProcessingStep[]): 'pending' | 'processing' | 'completed' | 'failed' => {
    if (steps.some(s => s.status === 'failed')) return 'failed';
    if (steps.every(s => s.status === 'completed')) return 'completed';
    if (steps.some(s => s.status === 'processing')) return 'processing';
    return 'pending';
  };

  const getStatusIcon = (status: ProcessingStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeVariant = (status: ProcessingStep['status']): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'failed':
        return 'destructive';
      case 'processing':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const calculateProgress = () => {
    let totalSteps = 0;
    let completedSteps = 0;
    
    pageProgress.forEach(page => {
      totalSteps += page.steps.length;
      completedSteps += page.steps.filter(s => s.status === 'completed').length;
    });

    return totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  };

  const getCompletedPages = () => {
    return Array.from(pageProgress.values()).filter(p => p.overallStatus === 'completed').length;
  };

  const getFailedPages = () => {
    return Array.from(pageProgress.values()).filter(p => p.overallStatus === 'failed').length;
  };

  const togglePageExpansion = (pageId: string) => {
    setExpandedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      return newSet;
    });
  };

  const sortedPages = Array.from(pageProgress.values()).sort((a, b) => {
    // Sort by status: processing first, then pending, completed, failed
    const statusOrder = { processing: 0, pending: 1, failed: 2, completed: 3 };
    return statusOrder[a.overallStatus] - statusOrder[b.overallStatus];
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "max-w-4xl max-h-[90vh]",
        isMinimized && "max-w-md max-h-[200px]"
      )}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Page Reprocessing Progress</DialogTitle>
              <DialogDescription>
                Processing {totalPages} pages through {PROCESSING_STEPS.length} steps each
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </Button>
          </div>
        </DialogHeader>

        {!isMinimized && (
          <>
            {/* Overall Progress */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Overall Progress</span>
                  <span>{calculateProgress().toFixed(0)}%</span>
                </div>
                <Progress value={calculateProgress()} className="h-2" />
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {getCompletedPages()}
                  </div>
                  <div className="text-sm text-muted-foreground">Completed</div>
                </Card>
                <Card className="p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {pageProgress.size - getCompletedPages() - getFailedPages()}
                  </div>
                  <div className="text-sm text-muted-foreground">In Progress</div>
                </Card>
                <Card className="p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {getFailedPages()}
                  </div>
                  <div className="text-sm text-muted-foreground">Failed</div>
                </Card>
              </div>

              {/* Page List */}
              <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                <div className="space-y-4">
                  {sortedPages.map(page => (
                    <Card key={page.pageId} className="p-4">
                      <div className="space-y-3">
                        {/* Page Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold truncate">
                              {page.title}
                            </h4>
                            <p className="text-xs text-muted-foreground truncate">
                              {page.url}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={getStatusBadgeVariant(page.overallStatus)}>
                              {page.overallStatus}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => togglePageExpansion(page.pageId)}
                            >
                              {expandedPages.has(page.pageId) ? 
                                <ChevronUp className="h-4 w-4" /> : 
                                <ChevronDown className="h-4 w-4" />
                              }
                            </Button>
                          </div>
                        </div>

                        {/* Progress Steps */}
                        {expandedPages.has(page.pageId) && (
                          <div className="grid grid-cols-2 gap-2 mt-3">
                            {page.steps.map(step => {
                              const Icon = step.icon;
                              return (
                                <div
                                  key={step.id}
                                  className="flex items-center gap-2 p-2 rounded-md bg-muted/50"
                                >
                                  <Icon className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-xs flex-1">{step.name}</span>
                                  {getStatusIcon(step.status)}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Quick Progress Bar */}
                        {!expandedPages.has(page.pageId) && (
                          <div className="flex gap-1">
                            {page.steps.map(step => (
                              <div
                                key={step.id}
                                className={cn(
                                  "h-1 flex-1 rounded-full",
                                  step.status === 'completed' && "bg-green-500",
                                  step.status === 'failed' && "bg-red-500",
                                  step.status === 'processing' && "bg-blue-500 animate-pulse",
                                  step.status === 'pending' && "bg-gray-200"
                                )}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </>
        )}

        {isMinimized && (
          <div className="space-y-2">
            <Progress value={calculateProgress()} className="h-2" />
            <p className="text-sm text-center text-muted-foreground">
              {getCompletedPages()} of {totalPages} pages completed
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}